<template>
  <modal-wrap
    class="customer-sender-addresses"
    :name="modalName"
    :header-class="'packing-slip-items-header'"
    body-class="packing-slip-items-modal"
    :hide-footer="true"
    size="md"
  >
    <template #modal-title>
      <div class="d-flex align-items-center">
        <mdicon name="CogOutline" size="22" class="dropdown-green" />
        <h3 class="ml-2 mb-0">Sender Addresses</h3>
      </div>
    </template>
    <template #default>
      <div class="sender-list-heading">
        <p>Choose the default sender or edit an address used for shipments.</p>
        <button class="btn btn-green" @click="openSenderAddressCreateModal">Add sender address</button>
      </div>
      <p v-if="!addresses.length">No sender addresses added yet.</p>
      <div v-else class="sender-list">
        <section v-for="address in addresses" :key="address.id" class="sender-list-item">
          <div>
            <strong>{{ address.companyName || address.label }}</strong>
            <b-badge v-if="address.isDefault" variant="success" class="ml-2">Default</b-badge>
            <b-badge v-if="address.hasCustomPrices" variant="success" class="ml-2">Sender prices</b-badge>
            <p>
              {{
                [address.addressLine, address.postalCode, address.city, address.countryCode].filter(Boolean).join(', ')
              }}
            </p>
          </div>
          <div class="sender-list-actions">
            <b-button variant="outline-secondary" @click="openSenderAddressEditModal(address)">Edit</b-button>
            <b-button
              v-if="!address.isDefault"
              variant="outline-secondary"
              @click="setSenderAddressAsDefault(address.id)"
            >
              Set as default
            </b-button>
            <b-button class="sender-delete" variant="outline-danger" @click="destroySenderAddress(address.id)">
              Delete
            </b-button>
          </div>
        </section>
      </div>
    </template>
  </modal-wrap>
</template>

<script lang="ts">
import type { KeyAccessor } from '@/types/Helpers/Arrays'
import type { Address } from '@/types/Models/Customer'
import { BBadge, BButton } from 'bootstrap-vue-next'
import { defineComponent, computed, onBeforeUnmount, type ComputedRef } from 'vue'
import { SENDER_ADDRESSES, SENDER_ADDRESS_ACTION } from '@/const/ModalTypes'
import { none } from '@/services/Helpers'
import { openModal } from '@/services/Modal'
import { useCustomersSenderAddressesStore } from '@/store/customers/senderAddresses'
import { useVueNextModalStore } from '@/store/vueNextModal'
import ModalWrap from '@/views/Modals/ModalWrap.vue'

export default defineComponent({
  name: 'SenderAddressesModal',
  components: { BBadge, BButton, ModalWrap },
  setup() {
    const modalStore = useVueNextModalStore()
    const senderAddressesStore = useCustomersSenderAddressesStore()
    const modalName: string = SENDER_ADDRESSES
    const modalData: ComputedRef<KeyAccessor> = computed(() => modalStore.modalPropsByName(modalName) || {})
    const addresses: ComputedRef<Address[]> = computed(() => senderAddressesStore.senderAddresses)

    const openSenderAddressCreateModal = (): void => {
      openModal(SENDER_ADDRESS_ACTION, { customerId: modalData.value.id })
    }

    const openSenderAddressEditModal = (address: Address): void => {
      openModal(SENDER_ADDRESS_ACTION, { customerId: modalData.value.id, id: address.id })
    }

    const showActionButtons = (addressId?: string): void => {
      if (!addressId) return
      document.getElementById(`element${addressId}`)?.classList.add('active')
    }

    const hideActionButtons = (addressId?: string): void => {
      if (!addressId) return
      document.getElementById(`element${addressId}`)?.classList.remove('active')
    }

    const destroySenderAddress = (addressId?: string): void => {
      if (!addressId) return
      openModal('confirmWrap', {
        message: 'The Sender Address will be deleted.',
        title: 'Are you sure you want to delete sender address?',
        okButtonText: 'Delete',
        onConfirm: () => {
          senderAddressesStore
            .destroySenderAddress({ companyId: modalData.value.id, addressId })
            .then(() => {
              senderAddressesStore.fetch(modalData.value.id).catch(none)
            })
            .catch(none)
        },
      })
    }

    const setSenderAddressAsDefault = (addressId?: string): void => {
      if (!addressId) return
      senderAddressesStore
        .setAsDefaultSenderAddress({ companyId: modalData.value.id, addressId })
        .then(() => {
          senderAddressesStore.fetch(modalData.value.id).catch(none)
        })
        .catch(none)
    }

    onBeforeUnmount(() => {
      senderAddressesStore.reset()
    })

    return {
      modalName,
      modalData,
      addresses,
      showActionButtons,
      hideActionButtons,
      destroySenderAddress,
      setSenderAddressAsDefault,
      openSenderAddressEditModal,
      openSenderAddressCreateModal,
    }
  },
})
</script>
